// Campaign API functions

/**
 * Creates a new campaign
 * @param token - Authentication token
 * @param campaignData - Campaign data to be created
 * @returns Created campaign data
 */
export const createCampaign = async (token: string, campaignData: any) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(campaignData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetches a campaign by ID
 * @param token - Authentication token
 * @param campaignId - ID of the campaign to fetch
 * @returns Campaign data
 */
export const fetchCampaignById = async (token: string, campaignId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns/${campaignId}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Updates an existing campaign
 * @param token - Authentication token
 * @param campaignId - ID of the campaign to update
 * @param campaignData - Updated campaign data
 * @returns Updated campaign data
 */
export const updateCampaign = async (token: string, campaignId: string, campaignData: any) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/campaigns/${campaignId}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(campaignData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetch opportunities with pagination
 * @param options - Options for fetching opportunities
 * @returns Paginated opportunities data
 */
export const fetchOpportunities = async ({ 
  pageParam = 1, 
  token, 
  searchQuery = "", 
  focusAreaFilters = [], 
  beneficiaryTypeFilters = [], 
  formatFilters = [],
  sgdsFilters = [],
  limit = 12
}: { 
  pageParam?: number, 
  token: string, 
  searchQuery?: string, 
  focusAreaFilters?: string[], 
  beneficiaryTypeFilters?: string[], 
  formatFilters?: string[],
  sgdsFilters?: string[],
  limit?: number
}) => {
  let url = `${import.meta.env.VITE_CC_API_URL}/v1/opportunities?page=${pageParam}&limit=${limit}`;
  
  // Add search query if provided
  if (searchQuery) {
    url += `&search=${encodeURIComponent(searchQuery)}`;
  }
  
  // Add focus area filters if provided
  if (focusAreaFilters.length > 0) {
    url += `&focus_area=${encodeURIComponent(focusAreaFilters.join(','))}`;
  }
  
  // Add beneficiary type filters if provided
  if (beneficiaryTypeFilters.length > 0) {
    url += `&beneficiary_type=${encodeURIComponent(beneficiaryTypeFilters.join(','))}`;
  }
  
  // Add format filters if provided
  if (formatFilters.length > 0) {
    url += `&format=${encodeURIComponent(formatFilters.join(','))}`;
  }
  
  // Add SDGs filters if provided
  if (sgdsFilters.length > 0) {
    url += `&sdgs=${encodeURIComponent(sgdsFilters.join(','))}`;
  }
  
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetches NPOs from the API
 * @param token - Authentication token
 * @returns Array of NPO data
 */
export const fetchNPOs = async (token: string): Promise<any[]> => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/npos`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data.data;
};

/**
 * Fetch curated NPOs for RFQ generation
 */
export async function fetchCuratedNPOs(token: string, page = 1, limit = 10, search = "") {
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/npos/corporate/curated-npos?page=${page}&limit=${limit}${searchParam}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch curated NPOs: ${response.status}`);
  }

  return await response.json();
}

/**
 * Generate RFQ for a campaign
 */
export async function generateRFQ(token: string, campaignId: string, npos: string[] = [], additionalNotes: string = "") {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaigns/${campaignId}/rfq`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        npos: npos,
        additional_notes: additionalNotes
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to generate RFQ: ${response.status}`);
  }

  return await response.json();
}

/**
 * Update RFQ additional notes
 */
export async function updateRFQNotes(token: string, campaignId: string, additionalNotes: string) {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/${campaignId}/rfq`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ 
        additional_notes: additionalNotes
      }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to update RFQ notes: ${response.status}`);
  }

  return await response.json();
}

/**
 * Fetch RFQs for NPO
 */
export async function fetchNpoRfqs(token: string, page = 1, limit = 10) {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq?page=${page}&limit=${limit}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch RFQs: ${response.status}`);
  }

  return await response.json();
}

/**
 * Fetch opportunity details by ID
 */
export async function fetchOpportunityById(token: string, opportunityId: string) {
  const params = new URLSearchParams();
  params.append('id', opportunityId);
  
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/opportunities/?${params.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch opportunity: ${response.status}`);
  }

  const responseData = await response.json();
  
  // The API returns an array with a single opportunity, so we need to extract it
  if (responseData.data && responseData.data.length > 0) {
    return {
      data: responseData.data[0],
      message: responseData.message || "Success"
    };
  }
  
  throw new Error("Opportunity not found");
}

/**
 * Fetch RFQ details by ID
 */
export async function fetchRfqById(token: string, rfqId: string) {
  const response = await fetch(
    `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch RFQ: ${response.status}`);
  }

  return await response.json();
}

/**
 * Submit RFQ response
 */
export async function submitRfqResponse(token: string, rfqId: string, rateCardData?: any) {
  // If rate card data is provided, save it
    const rateCardResponse = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/rate-card`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(rateCardData),
      }
    );
    
    if (!rateCardResponse.ok) {
      throw new Error(`Failed to save rate card: ${rateCardResponse.status}`);
    }
    
    const rateCardJson = await rateCardResponse.json();
    return { rateCardData: rateCardJson };
}

/**
 * Fetch rate card information for an RFQ
 * @param token - Authentication token
 * @param rfqId - ID of the RFQ to fetch rate card for
 * @returns Rate card data for the RFQ
 */
export const fetchRfqRateCard = async (token: string, rfqId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/rate-card`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Handle RFQ response acceptance or rejection
 * @param token - Authentication token
 * @param rfqId - ID of the RFQ
 * @param responseId - ID of the response to accept or decline
 * @param action - Action to take (accept or decline)
 * @returns Response data
 */
export const handleRfqResponse = async (token: string, rfqId: string, responseId: string, action: 'accept' | 'decline') => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/response/${responseId}/${action}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Accept an RFQ response
 * @param token - Authentication token
 * @param rfqId - ID of the RFQ
 * @param rateCardId - ID of the rate card to accept
 * @returns Response data
 */
export const acceptRfq = async (token: string, rfqId: string, rateCardId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/accept/${rateCardId}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Reject an RFQ response
 * @param token - Authentication token
 * @param rfqId - ID of the RFQ
 * @param rateCardId - ID of the rate card to reject
 * @param reason - Optional reason for rejection
 * @returns Response data
 */
export const rejectRfq = async (token: string, rfqId: string, rateCardId: string, reason: string = '') => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/rfq/${rfqId}/reject/${rateCardId}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ reason })
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetch NPO public profile by ID
 * @param token - Authentication token
 * @param npoId - ID of the NPO to fetch profile for
 * @returns NPO public profile data
 */
export const fetchNpoPublicProfile = async (token: string, npoId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/me/profile/public/npo/${npoId}`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetch NPOs for a specific campaign and opportunity
 * @param token - Authentication token
 * @param campaignId - ID of the campaign
 * @param opportunityId - ID of the opportunity
 * @returns List of NPOs that can support the specified opportunity in the campaign
 */
export const fetchCampaignOpportunityNpos = async (token: string, campaignId: string, opportunityId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/public/campaign/${campaignId}/opportunity/${opportunityId}/npos`;
  const response = await fetch(url, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};

/**
 * Fetch public campaign data by ID
 * @param campaignId - ID of the campaign to fetch
 * @returns Public campaign data including opportunities and NPOs
 */
export const fetchPublicCampaignById = async (campaignId: string) => {
  const url = `${import.meta.env.VITE_CC_API_URL}/v1/campaign/corporate/public/campaign/${campaignId}`;
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  
  const data = await response.json();
  return data;
};
